import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { AcButton } from '@/dashboard/ui';
import './consult-v14g.css';

export const CONSULT_STAGES = [
  { title: 'Questions', short: 'Questions' },
  { title: 'Examination', short: 'Examination' },
  { title: 'Plan & cost', short: 'Plan & cost' },
  { title: 'Consent, today’s work & medicines', short: 'Consent & today' },
  { title: 'Finish', short: 'Finish' },
] as const;
export type ConsultStage = 1 | 2 | 3 | 4 | 5;
export type ConsultStageStatus = 'ready' | 'open' | 'unknown';
export type ConsultStageStatuses = Partial<Record<ConsultStage, ConsultStageStatus>>;
export type ConsultStagePanels = readonly [ReactNode, ReactNode, ReactNode, ReactNode, ReactNode];

/** V14g presentation over the existing writers. Panels remain mounted while
 * hidden: navigation must not remount intake or abandon an in-flight line save.
 * The keyed inner boundary prevents a previous patient's completion advancing
 * the next visit. Navigation itself changes no clinical or saved-stage fact. */
export default function ConsultStages(props: {
  scopeKey: string;
  questionsTitle?: string;
  header?: ReactNode;
  notice?: ReactNode;
  footerSummary?: ReactNode;
  footerNote?: ReactNode;
  examinationSummary?: ReactNode;
  finishStatus?: ReactNode;
  headingAction?: (stage: ConsultStage, go: (stage: ConsultStage) => void) => ReactNode;
  additions?: boolean;
  additionStage?: ConsultStage;
  startAdditions?: boolean;
  stageStatuses?: ConsultStageStatuses;
  finalized: boolean;
  busy: boolean;
  onSave?: () => Promise<boolean>;
  saveStatus?: ReactNode;
  discard?: { visible: boolean; busy: boolean; onDiscard: () => Promise<boolean> };
  children: (go: (stage: ConsultStage) => void, finishActionHost: HTMLDivElement | null, completing: boolean, beginAdditions: (stage: ConsultStage) => void) => ConsultStagePanels;
}): JSX.Element {
  return <StageBoundary key={props.scopeKey} {...props} />;
}

function StageBoundary({ questionsTitle, additions, additionStage, startAdditions, stageStatuses, header, notice, footerSummary, footerNote, examinationSummary, finishStatus, headingAction, finalized, busy, onSave, saveStatus, discard, children }: {
  questionsTitle?: string;
  additions?: boolean;
  additionStage?: ConsultStage;
  startAdditions?: boolean;
  stageStatuses?: ConsultStageStatuses;
  finalized: boolean;
  busy: boolean;
  onSave?: () => Promise<boolean>;
  saveStatus?: ReactNode;
  discard?: { visible: boolean; busy: boolean; onDiscard: () => Promise<boolean> };
  children: (go: (stage: ConsultStage) => void, finishActionHost: HTMLDivElement | null, completing: boolean, beginAdditions: (stage: ConsultStage) => void) => ConsultStagePanels;
  header?: ReactNode;
  notice?: ReactNode;
  footerSummary?: ReactNode;
  footerNote?: ReactNode;
  examinationSummary?: ReactNode;
  finishStatus?: ReactNode;
  headingAction?: (stage: ConsultStage, go: (stage: ConsultStage) => void) => ReactNode;
}): JSX.Element {
  const [finishActionHost, setFinishActionHost] = useState<HTMLDivElement | null>(null);
  const [stage, setStage] = useState<ConsultStage>(finalized ? 5 : 1);
  const [completing, setCompleting] = useState(false);
  const openedAdditions=useRef(false);
  // A doctor's own stage choice wins: the arrival jump to the missing addition
  // never moves a doctor who already opened a stage before the additions read landed.
  const chosenStage=useRef(false);
  useEffect(()=>{if(additions&&startAdditions&&!openedAdditions.current){openedAdditions.current=true;setCompleting(true);if(!chosenStage.current)setStage(additionStage??1);}},[additions,additionStage,startAdditions]);
  const priorFinalized=useRef(finalized);
  useEffect(()=>{if(finalized&&!priorFinalized.current){setCompleting(false);setStage(5);}priorFinalized.current=finalized;},[finalized]);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const [discardReturn, setDiscardReturn] = useState(false);
  const alive = useRef(true);
  const pending = useRef(false);
  const currentStage = useRef(stage);
  const focusHeading = useRef(false);
  const root = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  currentStage.current = stage;
  useEffect(() => {
    const chrome = document.querySelector('.ac-app-topbar');
    const measure = () => {
      const top = chrome?.getBoundingClientRect().height ?? 0;
      root.current?.style.setProperty('--consult-header-top', `${top}px`);
      root.current?.style.setProperty('--consult-heading-offset', `${top + (headerRef.current?.getBoundingClientRect().height ?? 0) + 16}px`);
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (chrome) observer.observe(chrome);
    if (headerRef.current) observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);
  useLayoutEffect(() => {
    if (!focusHeading.current) return;
    focusHeading.current = false;
    // The new panel is committed now. Restore its heading before the user can
    // begin the next action; a deferred frame can steal focus from that input.
    const heading = root.current?.querySelector<HTMLElement>(`#consult-stage-${stage}-heading`);
    if (!heading) return;
    // Match the frozen stage navigation: show the whole patient/navigation
    // context, then focus the new heading without scrolling it under chrome.
    window.scrollTo({ top: 0, behavior: 'instant' });
    heading.focus({ preventScroll: true });
  }, [stage, completing]);
  useEffect(() => {
    // Restore resolves before every child has committed its cleared draft.
    // Wait for the actual button removal, not a guessed animation-frame delay.
    if (!discardReturn || discard?.visible) return;
    setDiscardReturn(false);
    root.current?.querySelector<HTMLElement>(`#consult-stage-${currentStage.current}-heading`)?.focus({ preventScroll: true });
  }, [discardReturn, discard?.visible]);
  const go = (next: ConsultStage) => {
    if (pending.current || busy) return;
    if (finalized && additions && next !== 5) setCompleting(true);
    chosenStage.current = true;
    focusHeading.current = next !== stage;
    setStage(next);
    setFailed(false);
  };
  const save = async () => {
    if (pending.current || busy || (finalized && !additions) || !onSave) return;
    const startedAt = currentStage.current;
    pending.current = true;
    setSaving(true);
    setFailed(false);
    try {
      const confirmed = await onSave();
      if (!alive.current) return;
      if (!confirmed) { setFailed(true); return; }
      if (finalized && startedAt === 5) { setCompleting(false); root.current?.querySelector<HTMLElement>('[data-testid="consult-close"] button')?.focus(); }
      // No "complete" checkmark is inferred from flushing: unanswered clinical
      // facts remain unanswered and Finish uses its existing warning/close flow.
      if (currentStage.current === startedAt && startedAt < 5) {
        focusHeading.current = true;
        setStage((startedAt + 1) as ConsultStage);
      }
    } catch {
      if (alive.current) setFailed(true);
    } finally {
      pending.current = false;
      if (alive.current) setSaving(false);
    }
  };
  const beginAdditions = (next: ConsultStage) => {
    if (!additions || pending.current || busy) return;
    setCompleting(true); chosenStage.current = true; focusHeading.current = true; setStage(next); setFailed(false);
  };
  const panels = children(go, finishActionHost, completing, beginAdditions);
  const finishedSummary = finalized && !completing && stage === 5;
  return <div ref={root} className="consult-v14g" data-testid="consult-stages" data-stage={stage} onFocusCapture={event=>{
    const control=event.target;
    // The existing sticky chrome must not conceal a keyboard-focused clinical
    // control. Preserve focus; scroll only the actual stage, never a portal.
    if(!(control instanceof HTMLElement)||!root.current?.contains(control)||!control.closest('.consult-v14g__stage')||!control.matches(':focus-visible')||!control.matches('button,a,input,select,textarea,summary,[tabindex]'))return;
    const bounds=control.getBoundingClientRect();
    const top=(headerRef.current?.getBoundingClientRect().bottom??0)+12;
    const bottom=(root.current.querySelector('.consult-v14g__actions')?.getBoundingClientRect().top??window.innerHeight)-12;
    if(bounds.bottom>bottom)window.scrollBy({top:bounds.bottom-bottom,behavior:'instant'});
    else if(bounds.top<top)window.scrollBy({top:bounds.top-top,behavior:'instant'});
  }}>
    <header ref={headerRef} className="consult-v14g__header">
      {header}
    <nav className="consult-v14g__strip" aria-label="Consultation stages">
      {CONSULT_STAGES.map((item, index) => {
        const n = (index + 1) as ConsultStage;
        const status = n === 5 && finalized ? 'ready' : stageStatuses?.[n];
        return <AcButton key={n} variant="ghost" className={status==='ready'?'is-ready':status==='open'?'is-open':undefined} title={status==='open'?'Review missing or unsaved information':undefined} aria-label={status&&status!=='unknown'?`${item.title} · ${status==='ready'?'saved':'needs attention'}`:undefined} aria-current={stage === n ? 'step' : undefined}
          disabled={saving || busy} onClick={() => go(n)} data-testid={`consult-stage-go-${n}`}>
          <span className="consult-v14g__number" aria-hidden="true">{status==='ready'?'✓':status==='open'?'!':n}</span>
          <span className="consult-v14g__full-title">{item.title}</span>
          <span className="consult-v14g__short-title">{item.short}</span>
        </AcButton>;
      })}
    </nav>
    </header>
    {!finishedSummary ? notice : null}
    {panels.map((panel, index) => <section key={index} className="consult-v14g__stage"
      hidden={stage !== index + 1} aria-labelledby={`consult-stage-${index + 1}-heading`}
      data-testid={`consult-stage-${index + 1}`}>
      <div className="consult-v14g__stage-heading" hidden={index===4&&finishedSummary}><div><h2 id={`consult-stage-${index + 1}-heading`} tabIndex={-1}>{index===0 ? questionsTitle ?? 'Questions' : CONSULT_STAGES[index].title}</h2>
      {index === 2 ? <p>Today’s charges and the treatment plan in one list.</p> : null}{index === 4 && !finalized ? <p>Check, set the return visit, finish.</p> : null}</div>{headingAction?.((index+1) as ConsultStage,go)}</div>
      {index === 3 ? <p className="consult-v14g__stage-description">In this order: one treatment consent, then what you did today, then medicines.</p> : null}
      {panel}
    </section>)}
    {footerNote ? <div className="consult-v14g__footer-note">{footerNote}</div> : null}
    {(!finalized || (additions && completing)) ? <footer className="consult-v14g__actions" data-testid="consult-stage-footer">
      {stage===3 ? footerSummary : null}
      {stage===5 ? <div className="consult-v14g__finish-status">{finishStatus}</div> : null}
      <div className={`consult-v14g__save-status ${(stage===3||stage===5)&&!failed&&!saving?'sr-only':''}`} aria-live="polite">
        {failed ? <p role="alert">Save is not confirmed. Your changes are still here. Check the highlighted fields or retry.</p> : saving ? 'Saving…' : stage===2 ? examinationSummary ?? saveStatus : saveStatus}
      </div>
      {discard?.visible ? <AcButton key="discard" variant="ghost" disabled={saving || busy || discard.busy} onClick={() => {
        void discard.onDiscard().then(restored => {
          if (restored && alive.current) { setFailed(false); setDiscardReturn(true); }
        });
      }} data-testid="consult-stage-discard">Discard changes</AcButton> : null}
      {stage > 1 ? <AcButton key="back" variant="secondary" disabled={saving || busy} onClick={() => go((stage - 1) as ConsultStage)}>Back</AcButton> : null}
      {stage < 5 || additions ? <AcButton key="save" variant="primary" disabled={saving || busy || !onSave} onClick={() => void save()}
        data-testid="consult-stage-save">{saving ? 'Saving…' : failed ? 'Retry save' : stage===5&&additions?'Save additions':`Save & continue to ${CONSULT_STAGES[Math.min(stage, 4)]!.short.toLowerCase()}`}</AcButton> : null}
      <div ref={setFinishActionHost} hidden={stage !== 5} className="consult-v14g__finish-action" />
    </footer> : null}
  </div>;
}
